import { useState } from "react";

import { T } from "../strings";
import * as api from "../api/client";
import type { AuthUser } from "../api/types";

interface Props {
  onSignedIn: (user: AuthUser) => void;
  /** Set when a session ended on its own, so the screen can say why it is showing. */
  notice?: string | null;
}

/**
 * The only screen reachable without a session, and it does exactly one thing: log in.
 *
 * There is no registration form because there is no registration endpoint. Accounts are
 * created by the owner from a shell (`backend/scripts/set_password.py`) and handed over in
 * person, so a "create access" tab here could only ever fail.
 */
export function LoginScreen({ onSignedIn, notice }: Props): React.ReactElement {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      onSignedIn(await api.login(login.trim(), password));
    } catch (cause) {
      setError(messageFor(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="k-login">
      <form className="k-login__panel" onSubmit={(event) => void submit(event)}>
        <div className="k-login__mark">
          <span className="k-login__title">{T.app.title}</span>
          <span className="k-login__subtitle">{T.auth.subtitle}</span>
        </div>

        {/* `htmlFor` rather than a wrapping <label>: inside a <label> the hint would become
            part of the input's accessible name — a screen reader would read the whole rule
            as the field title. */}
        <div className="k-field">
          <label className="k-label" htmlFor="auth-login">
            {T.auth.login}
          </label>
          <input
            id="auth-login"
            className="k-input"
            name="login"
            autoComplete="username"
            autoFocus
            required
            value={login}
            onChange={(event) => setLogin(event.target.value)}
          />
        </div>

        <div className="k-field">
          <label className="k-label" htmlFor="auth-password">
            {T.auth.password}
          </label>
          <input
            id="auth-password"
            className="k-input"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        {notice !== null && notice !== undefined && error === null && (
          <p className="k-note k-note--warn">{notice}</p>
        )}
        {error !== null && (
          <p className="k-note k-note--error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="k-btn k-btn--primary k-btn--block" disabled={busy}>
          {busy ? T.auth.working : T.auth.submitLogin}
        </button>

        <p className="k-login__why">{T.auth.why}</p>
        <p className="k-login__why">{T.auth.noSelfSignup}</p>
      </form>
    </div>
  );
}

/**
 * Text for a failed attempt, keyed on the server's machine-readable `reason` rather than on
 * its English message — the message is developer-facing and lives in the logs.
 */
function messageFor(cause: unknown): string {
  if (cause instanceof api.ApiError) {
    if (cause.isUnauthorized) return T.auth.invalidCredentials;
    return cause.message;
  }
  return cause instanceof Error ? cause.message : T.common.unknownError;
}
